import { Suspense, lazy, useState, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Onboarding, useOnboarding } from "@/components/Onboarding";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ConnectionRestoredToast } from "@/components/ConnectionRestoredToast";
import { Toaster } from "@/components/ui/toaster";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const FaceIdLogin = lazy(() => import("./pages/FaceIdLogin"));
const Transactions = lazy(() => import("./pages/Transactions"));
const AddTransaction = lazy(() => import("./pages/AddTransaction"));
const Categories = lazy(() => import("./pages/Categories"));
const EditCategory = lazy(() => import("./pages/EditCategory"));
const CategorySelect = lazy(() => import("./pages/CategorySelect"));
const AccountingSettings = lazy(() => import("./pages/AccountingSettings"));
const Settings = lazy(() => import("./pages/Settings"));
const Tags = lazy(() => import("./pages/Tags"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Notifications = lazy(() => import("./pages/Notifications"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const Budgets = lazy(() => import("./pages/Budgets"));
const EditBudget = lazy(() => import("./pages/EditBudget"));
const UserSettings = lazy(() => import("./pages/UserSettings"));
const Help = lazy(() => import("./pages/Help"));
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// App content with onboarding check
function AppContent() {
  const { isCompleted } = useOnboarding();
  const [showOnboarding, setShowOnboarding] = useState(!isCompleted);

  // Check onboarding status on mount
  useEffect(() => {
    setShowOnboarding(!isCompleted);
  }, [isCompleted]);

  if (showOnboarding) {
    return <Onboarding onComplete={() => setShowOnboarding(false)} />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/login" element={<FaceIdLogin />} />
        <Route path="/face-id" element={<FaceIdLogin />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/transactions/new" element={<AddTransaction />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/categories/edit/:id" element={<EditCategory />} />
        <Route path="/categories/new" element={<EditCategory />} />
        <Route path="/category-select" element={<CategorySelect />} />
        <Route path="/accounting-settings" element={<AccountingSettings />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/tags" element={<Tags />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/notification-settings" element={<NotificationSettings />} />
        <Route path="/budgets" element={<Budgets />} />
        <Route path="/budgets/new" element={<EditBudget />} />
        <Route path="/budgets/edit/:id" element={<EditBudget />} />
        <Route path="/user-settings" element={<UserSettings />} />
        <Route path="/help" element={<Help />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <OfflineBanner />
          <ConnectionRestoredToast />
          <AppContent />
          <Toaster />
        </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
